import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { action } from '../lib/api.js';

const stableObject = {};
const stableArray = [];

const useBoundForm = ({ id, initialState, onChange, callback }) => {
  const formId = useRef(id || Math.floor(Math.random() * 1000000));

  const state = useSelector((state) => (state.ui.boundForms[formId.current] || stableObject).state || stableObject);
  const invalids = useSelector((state) => (state.ui.boundForms[formId.current] || stableObject).invalids || stableArray);
  const empties = useSelector((state) => (state.ui.boundForms[formId.current] || stableObject).empties || stableArray);
  const validating = useSelector((state) => (state.ui.boundForms[formId.current] || stableObject).validating);
  const stateRef = useRef(null);

  const isValid = invalids.length < 1 && empties.length < 1;

  const setState = (payload, callback) => {
    const handleCallback = (data) => {
      if (typeof callback === 'function') {
        callback(data?.state || {});
      }
    };

    if (typeof payload === 'function') {
      action('uiPatchFormState', { formId: formId.current, state: payload(state) }, handleCallback);
      return;
    }
    action('uiPatchFormState', { formId: formId.current, state: payload }, handleCallback);
  };

  useEffect(() => {
    action('uiPatchFormState', { formId: formId.current, state: initialState || {} });
    return () => {
      action('uiClearFormState', formId.current);
    };
  }, []);

  useEffect(() => {
    if (validating === false && (invalids.length > 0 || empties.length > 0)) {
      const id = empties[0] || invalids[0];
      const input = document.querySelector(`input[name="${id}"]`);
      if (input) {
        input.focus();
      }
    }
  }, [validating]);

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (callback) {
      callback();
    }
  };

  const set = (key, value, callback) => {
    if (typeof key === 'object') {
      return;
    }
    setState({ [key]: value }, callback);
    const empty = !value;
    action(empty ? 'uiSetFormEmptyField' : 'uiRemoveFormEmptyField', {
      formId: formId.current,
      fieldId: key
    });
  };

  const validate = () => {
    action('uiToggleFormValidation', { formId: formId.current, validating: true });
    setTimeout(() => {
      action('uiToggleFormValidation', { formId: formId.current, validating: false });
    }, 0);
  };

  const invalidate = (id, data = {}) => {
    action('uiSetFormInvalidField', {
      formId: formId.current,
      fieldId: id,
      message: data.message
    });
  };

  const reset = (state) => {
    action('uiClearFormState', formId.current);
    action('uiPutFormState', { formId: formId.current, state: state || initialState || {} });
    setTimeout(() => {
      action('uiToggleFormReset', { formId: formId.current, reset: true });
      setTimeout(() => {
        action('uiToggleFormReset', { formId: formId.current, reset: false });
      }, 0);
    }, 0);
  };

  useEffect(() => {
    if (stateRef.current === null || Object.keys(stateRef.current).length < 1) {
      stateRef.current = state;
      return;
    }
    // Fix for circular structure: use shallow comparison of keys and values
    const stateKeys = Object.keys(state);
    const refKeys = Object.keys(stateRef.current || {});
    if (stateKeys.length === refKeys.length && stateKeys.every((key) => state[key] === stateRef.current[key])) {
      stateRef.current = state;
      return;
    }
    if (onChange) {
      onChange?.(state);
    }
    stateRef.current = state;
  }, [state]);

  return {
    id: formId.current,
    handleSubmit,
    isValid,
    invalidate,
    reset,
    invalids,
    empties,
    set,
    state,
    setState,
    validate
  };
};

export default useBoundForm;
